import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { authService } from "../../services/auth.service";
import { useAuth } from "../../context/AuthContext";
import { tagOf } from "../../utils/api-error";
import { otpSchema } from "./validations/auth.schema";
import { OtpPurpose } from "../../interfaces/user.interface";

// docs/web-api-spec.md §POST /auth/verify-otp and §POST /auth/resend-otp.
const KNOWN_TAGS = [
  "invalid_otp",
  "expired_otp",
  "email_taken",
  "rate_limited",
];

// Web/api enforces exactly 60 seconds per email; asking sooner earns
// `rate_limited`, so the button says no before the request does.
const RESEND_COOLDOWN_SECONDS = 60;

const VerifyOtp = () => {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const state = useLocation().state as {
    tempToken?: string;
    email?: string;
    purpose?: OtpPurpose;
    from?: string;
  } | null;
  const purpose = state?.purpose ?? "register";

  const [failureTag, setFailureTag] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: yupResolver(otpSchema) });

  useEffect(() => {
    if (cooldown === 0) return;
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const tempToken = state?.tempToken;

  const onSubmit = handleSubmit(async ({ otp }) => {
    if (!tempToken) return;
    setFailureTag(null);
    try {
      const result = await authService.verifyOtp(tempToken, otp, purpose);
      setUser(result.user);
      navigate(state?.from ?? "/account", { replace: true });
    } catch (err) {
      setFailureTag(tagOf(err) ?? "generic");
    }
  });

  const onResend = async () => {
    if (!tempToken) return;
    setFailureTag(null);
    setResent(false);
    try {
      await authService.resendOtp(tempToken, purpose);
      setResent(true);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      const tag = tagOf(err);
      setFailureTag(tag ?? "generic");
      // A rejected resend still starts the cooldown when the server's reason
      // *is* the cooldown — otherwise the button stays live and the user
      // keeps hammering an endpoint that will keep refusing.
      if (tag === "rate_limited") setCooldown(RESEND_COOLDOWN_SECONDS);
    }
  };

  // A direct visit or a refresh loses the tempToken along with the router
  // state. Rendering a form that cannot possibly submit only wastes a click.
  if (!tempToken) return <Navigate to="/register" replace />;

  return (
    <div className="card p-8 max-w-md mx-auto mt-10">
      <h1 className="mb-2 font-display text-2xl font-extrabold">
        {t(purpose === "login" ? "verify.loginTitle" : "verify.title")}
      </h1>
      <p className="mb-6 text-sm text-ink-soft">
        {t("verify.body")}
        {state?.email ? ` ${state.email}` : ""}
      </p>

      {failureTag && (
        <p
          role="alert"
          className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          {KNOWN_TAGS.includes(failureTag)
            ? t(`errors.${failureTag}`)
            : t("common:errors.generic")}
        </p>
      )}

      {resent && (
        <p className="mb-4 rounded-2xl border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          {t("verify.resent")}
        </p>
      )}

      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <div>
          <label htmlFor="otp" className="mb-1 block text-sm font-bold">
            {t("fields.otp")}
          </label>
          <input
            id="otp"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="field-input"
            {...register("otp")}
          />
          {errors.otp?.message && (
            <p className="mt-1 text-sm text-red-600">{t(errors.otp.message)}</p>
          )}
        </div>

        <button type="submit" disabled={isSubmitting} className="btn w-full">
          {t("verify.submit")}
        </button>
      </form>

      <button
        type="button"
        onClick={onResend}
        disabled={cooldown > 0}
        className="mt-4 text-sm font-bold text-teal-dark underline disabled:no-underline disabled:opacity-50"
      >
        {cooldown > 0
          ? `${t("verify.resend")} (${cooldown})`
          : t("verify.resend")}
      </button>
    </div>
  );
};

export default VerifyOtp;
